import groupBy from "lodash/groupBy";
import uniqBy from "lodash/uniqBy";
import omit from "lodash/omit";
import type { CampaignUser, MenuItem as CampaignUserMenuItem } from "../../../types/campaign";
import type { SocialGiftBundleItem } from "../../../types/campaign";
import { navigateToPath } from "../../routing";
import PATHS from "../../constants/routePaths";
import sortBy from "lodash/sortBy";

export type Variation = {
  visible_name: string;
  name: string;
  isAvailable: boolean;
  image?: string;
  itemKey?: string;
  isDefaultImage?: boolean;
  api?: string;
  order: number;
};

export type MenuItem = {
  variations: Variation[];
  secondVariations?: Variation[];
  visible_name: string;
  visible_variation?: string;
  visible_variation_2?: string;
  order: number;
  name: string;
  variation?: string;
  description?: string | undefined;
  image?: string | undefined;
  emoji?: string | undefined | null;
  itemKey?: string;
  hasMultipleOptions?: boolean;
};

export type MenuData = {
  menuItems: MenuItem[];
  socialGiftBundleItems: SocialGiftBundleItem[];
};

export function buildMenuItems(campaignUser: CampaignUser): MenuData | null {
  const { metadata } = campaignUser;
  if (!metadata) {
    return null;
  }
  const storeItems = metadata.available_menu_items ?? [];

  // Turns out we just need to sort once here, because the order will be kept along the way
  const menuItems: CampaignUserMenuItem[] = (metadata.menu_items ?? []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const itemKeyVariations = groupBy(
    menuItems.map((item) => {
      return {
        visible_name: item.visible_name ?? item.name,
        visible_variation: item.visible_variation ?? item.variation,
        visible_variation_2: item.visible_variation_2,
        name: item.name,
        variation: item.variation,
        image: item.image,
        is_default_image: item.is_default_image,
        order: item.order
      };
    }),
    "name"
  );

  const uniqMenuItemsByItemKey = uniqBy(menuItems, "name");

  const uniqMenuItemsByItemKeyWithVariations = uniqMenuItemsByItemKey.map((uniqMenuItem) => {
    const { variations, secondVariations } = itemKeyVariations[uniqMenuItem.name].reduce<Record<string, Variation[]>>(
      (acc, itemKeyVariation) => {
        const storeItem = storeItems.find(
          (item) => item.name_key == uniqMenuItem.name && item.variation_key == itemKeyVariation.variation
        );
        acc.variations.push({
          visible_name: itemKeyVariation.visible_variation ?? itemKeyVariation.variation,
          name: itemKeyVariation.variation,
          isAvailable: !!storeItem,
          image: itemKeyVariation?.image,
          itemKey: storeItem?.item_key,
          order: itemKeyVariation.order ?? 0,
          isDefaultImage: itemKeyVariation.is_default_image,
          api: storeItem?.pickup_location?.api
        });
        if (itemKeyVariation.visible_variation_2) {
          acc.secondVariations.push({
            visible_name: itemKeyVariation.visible_variation_2,
            name: itemKeyVariation.variation,
            isAvailable: !!storeItem,
            image: itemKeyVariation?.image,
            itemKey: storeItem?.item_key,
            order: itemKeyVariation.order ?? 0
          });
        }
        return acc;
      },
      { variations: [], secondVariations: [] }
    );

    const itemKey = variations.find((variation) => variation.isAvailable)?.itemKey;
    const defaultImage = variations.find((variation) => variation.isDefaultImage)?.image;

    return {
      ...omit(uniqMenuItem, ["variation", "visible_variation"]),
      image: defaultImage ?? uniqMenuItem.image,
      visible_name: uniqMenuItem.visible_name ?? uniqMenuItem.name,
      variations,
      secondVariations,
      itemKey,
      hasMultipleOptions: secondVariations.length > 0,
      order: uniqMenuItem.order ?? 0
    };
  });

  return {
    menuItems: uniqMenuItemsByItemKeyWithVariations,
    socialGiftBundleItems: metadata.social_gift_bundle?.items ?? []
  };
}

export const BundleHelpers = {
  getUnitaryMenuItems: (menuData: MenuData): SocialGiftBundleItem[] => {
    return menuData.socialGiftBundleItems.reduce((acc: SocialGiftBundleItem[], item) => {
      for (let i = 0; i < item.count; i++) {
        acc.push({ ...item, count: 1 });
      }

      return acc;
    }, []);
  }
};

export const buildSocialGiftBundleMenu = (menuData: MenuData): MenuItem[] => {
  const unitaryMenuItems = BundleHelpers.getUnitaryMenuItems(menuData);

  try {
    return unitaryMenuItems.map((unitaryItem) => {
      const menuItem = menuData.menuItems.find((menuItem) => menuItem.itemKey === unitaryItem.item_key);

      if (!menuItem) {
        throw new Error(`Could not find menuItem for item_key: ${unitaryItem.item_key}`);
      }

      const bundleMenuItem: MenuItem = {
        ...menuItem
      };

      if (unitaryItem.variation) {
        const variation = menuItem.variations.find((variation) => variation.name === unitaryItem.variation);

        if (!variation) {
          throw new Error(`Could not find variation for variation_key: ${unitaryItem.variation}`);
        }

        bundleMenuItem.variations = [variation];
      }

      return bundleMenuItem;
    });
  } catch (error) {
    // If we can't find the menu items, it means that no available variation was found for the item
    // If we can't find the variation, it means that the variation was removed from the stocks
    // FIXME: maybe we should have a custom error here, so we only handle the stock out for those, bugsnag the rest
    navigateToPath(PATHS.STOCK_OUT);
    return [];
  }
};

type SubVariation = {
  name: string;
  visible_name: string;
  isAvailable: boolean;
  order?: number;
};

const mapVariations = (variations: Variation[] = []) => {
  return variations.reduce((acc, variation) => {
    acc[variation.name] = variation;
    return acc;
  }, {} as Record<string, Variation>);
};

enum VariationIdx {
  FIRST = 0,
  SECOND = 1
}

type VariationIndex = VariationIdx.FIRST | VariationIdx.SECOND;

export const VariationAvailabilityHelpers = {
  isAvailable: ({
    name,
    variationIndex,
    item,
    selectedVariation
  }: {
    name: string;
    variationIndex: VariationIndex;
    item: MenuItem;
    selectedVariation?: string;
  }) => {
    const variationMap = mapVariations(item.variations);

    if (!selectedVariation && item.hasMultipleOptions) {
      return item.variations.some(
        (variation) => variation.name.split("|")[variationIndex] === name && variation.isAvailable
      );
    } else if (item.hasMultipleOptions) {
      const [selectedFirstVariation, selectedSecondVariation] = selectedVariation!.split("|");
      const variationItem =
        variationIndex === VariationIdx.FIRST
          ? variationMap[`${name}|${selectedSecondVariation}`]
          : variationMap[`${selectedFirstVariation}|${name}`];

      // FIXME: Why do we get items without itemKey and isAvailable: true
      // It happens when selecting multiple variation items, but works fine on reload
      // (the item is still there, but isAvailable is false on reload)
      return Boolean(variationItem?.isAvailable && variationItem?.itemKey);
    } else {
      return variationMap[name]?.isAvailable;
    }
  }
};

// What this does is build a list of unique variations for each option
// e.g.:
//   If you have Red|Small, Red|Large, Blue|Small, Blue|Large, you want to get Red, Blue for the first variation
//   and Small, Large for the second variation. This returns and object with that, it's visible name.
//
// The availability is tied to the merged variations, so we need to assign the isAvailable depending on what
// the selected variation is. So, if you have Red|Small unavailable, but both Red|Large and Blue|Small are available,
// when nothing is selected, Red should be available. But, if selecting Blue|Small, because Red|Small is unavailable,
// Red should become unavailable.
//
// So, we pass all the information we need here:
// variations: the list we're checking (item.variations or item.secondVariations)
// variationIndex: the index of the variation we're checking (0 for first, 1 for second, as FIRST|SECOND)
// item: the menu item we're checking (from which we pulled the variations from)
// selectedVariation: the variation that is currently selected (if any)
//
// The is availability then uses the selectedVariation to check
//  - a first variation relative to the second selected variation
//  - a second variation relative to the first selected variation
// It will also handle not having multiple options or not having a selected variation
const getUniqueVariationForContext = ({
  variations,
  variationIndex,
  item,
  selectedVariation
}: {
  variations: Variation[];
  variationIndex: VariationIndex;
  item: MenuItem;
  selectedVariation?: string;
}) => {
  return variations.reduce((acc, variation) => {
    const splitVariationKey = variation.name.split("|")[variationIndex];
    acc.find((accVariation) => accVariation.name === splitVariationKey) ||
      acc.push({
        name: splitVariationKey,
        visible_name: variation.visible_name,
        order: variation.order,
        isAvailable: VariationAvailabilityHelpers.isAvailable({
          name: splitVariationKey,
          variationIndex,
          item,
          selectedVariation
        })
      });
    return acc;
  }, [] as SubVariation[]);
};

export function getUniqueByNameFirstVariations(item: MenuItem, selectedVariation?: string) {
  if (!item.hasMultipleOptions) return sortBy(item.variations, "order");
  return getUniqueVariationForContext({
    variations: sortBy(item.variations, "order"),
    variationIndex: VariationIdx.FIRST,
    item,
    selectedVariation
  });
}

export function getUniqueByNameSecondVariations(item: MenuItem, selectedVariation?: string) {
  if (!item.hasMultipleOptions) return [];
  return getUniqueVariationForContext({
    variations: sortBy(item.secondVariations ?? [], "order"),
    variationIndex: VariationIdx.SECOND,
    item,
    selectedVariation
  });
}

export function buildVariations(item: MenuItem | undefined) {
  if (!item?.hasMultipleOptions) return { variations: item?.variations ?? [], secondVariationsByFirstVariation: {} };

  const secondVariationsByFirstVariation: Record<string, Variation[]> = {};

  const variations = uniqBy(item.variations, "visible_name");
  variations.forEach((variation) => {
    const firstVariation = variation.name?.split("|")[0];
    const secondVariations =
      item.secondVariations?.filter((secondVariation) => secondVariation.name.startsWith(firstVariation)) ?? [];
    secondVariationsByFirstVariation[firstVariation] = sortBy(secondVariations, (variation) =>
      variation.order ?? variation.visible_name
    );
    variation.isAvailable = secondVariations.some((secondVariation) => secondVariation.isAvailable);
  });

  const sortedVariations = sortBy(variations, (variation) =>
    variation.order ?? variation.visible_name
  );
  return { variations: sortedVariations, secondVariationsByFirstVariation };
}

export function getImage({
  defaultImage,
  selectedVariation,
  firstVariation,
  secondVariationsByFirstVariation
}: {
  defaultImage: string | undefined;
  selectedVariation: Variation | undefined;
  firstVariation: string | undefined;
  secondVariationsByFirstVariation: Record<string, Variation[]>;
}): string | undefined {
  let image;
  if (selectedVariation) {
    image = selectedVariation.image;
  } else if (firstVariation) {
    image = secondVariationsByFirstVariation[firstVariation][0]?.image;
  }
  return image ?? defaultImage;
}

export const isAvailable = (item: MenuItem) => item.variations?.some((variation) => variation.isAvailable);
